import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildWebSocketUrl } from '../utils/websocket';
import { API_URL } from '../utils/api';

const WS_RECONNECT_DELAY = 3000;
const WS_HEARTBEAT_INTERVAL = 30000;

/**
 * Hook for presence tracking, online users list, and direct messaging via WebSocket.
 *
 * Sprint 5 (player_id-cutover): routing-nyckel är `presence_key = player_id ||
 * discord_id`. Backend (`backend/routers/presence.py`) accepterar redan båda och
 * föredrar player_id; denna hook och OnlineSidebar väljer samma nyckel
 * deterministiskt så DM-routing matchar. Steam-only users (inget discord_id)
 * blir därmed delaktiga i presence/DM. Befintliga DMs i Django (lagrade på
 * `from_discord_id`/`to_discord_id`) hamnar i en parallell namespace tills
 * DirectMessage-modellen renamas — det är acceptabel tradeoff i denna scope-runda.
 */
export function usePresence() {
  const { user, isLoggedIn, getAuthHeaders } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState([]);
  // messages/unreadCount/activeChat keyas på presence_key (player_id || discord_id),
  // samma nyckel som backend använder för `dm:{key}` Redis-channels.
  const [messages, setMessages] = useState({});
  const [activeChat, setActiveChat] = useState(null);
  const [unreadCount, setUnreadCount] = useState({});

  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectRef = useRef(null);
  const activeChatRef = useRef(activeChat);

  // Keep ref in sync so WS handler sees current value
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  // Clear unread when opening a chat
  useEffect(() => {
    if (activeChat) {
      setUnreadCount(prev => {
        const next = { ...prev };
        delete next[activeChat];
        return next;
      });
    }
  }, [activeChat]);

  /**
   * Build the identify/heartbeat payload sent to the presence WebSocket.
   *
   * Skickar player_id primärt + discord_id som legacy fallback — backend väljer
   * `player_id or discord_id` som presence_key. Tomma strängar om fältet saknas
   * (steam-only user → ingen discord_id; gammal user utan player_id → ingen pk).
   */
  const getUserInfo = useCallback(() => {
    if (!user) return null;
    return {
      player_id: user.player_id ? String(user.player_id) : '',
      discord_id: user.discord_id ? String(user.discord_id) : '',
      steam_id: user.steam_id || '',
      steam_username: user.steam_username || user.discord_username || '',
      steam_avatar: user.steam_avatar || '',
      discord_username: user.discord_username || '',
    };
  }, [user]);

  /**
   * Få min egen presence_key (player_id om finns, annars discord_id).
   *
   * Matchar exakt vad backend bygger — kritiskt för att `loadHistory(my_id=...)`
   * och DM-routing ska träffa rätt rader.
   */
  const getMyPresenceKey = useCallback(() => {
    if (!user) return '';
    return user.player_id ? String(user.player_id) : String(user.discord_id || '');
  }, [user]);

  const sendMessage = useCallback((targetId, text) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: 'dm',
      to: String(targetId),
      text,
    }));
  }, []);

  // Load chat history when opening a conversation
  const loadHistory = useCallback(async (targetId) => {
    if (!user) return;
    const myId = getMyPresenceKey();
    if (!myId) return;
    try {
      const resp = await fetch(
        `${API_URL}/api/presence/messages/${targetId}?my_id=${myId}`,
        { headers: getAuthHeaders() }
      );
      if (resp.ok) {
        const data = await resp.json();
        setMessages(prev => ({
          ...prev,
          [targetId]: data.messages || [],
        }));
      }
    } catch (e) {
      console.error('Failed to load chat history:', e);
    }
  }, [user, getAuthHeaders, getMyPresenceKey]);

  // Open chat with a specific user
  const openChat = useCallback((targetId) => {
    setActiveChat(targetId);
    loadHistory(targetId);
  }, [loadHistory]);

  const connect = useCallback(() => {
    const info = getUserInfo();
    if (!info) return;

    const wsUrl = buildWebSocketUrl('/api/presence/ws');
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Send identify message
        ws.send(JSON.stringify({ type: 'identify', ...info }));

        // Start heartbeat
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'heartbeat' }));
          }
        }, WS_HEARTBEAT_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'presence') {
            setOnlineUsers(msg.online || []);
          } else if (msg.type === 'dm') {
            // Incoming DM from someone else
            const dm = msg.message;
            const fromId = dm.from;
            setMessages(prev => ({
              ...prev,
              [fromId]: [...(prev[fromId] || []), dm],
            }));
            // Increment unread if not viewing that chat
            if (activeChatRef.current !== fromId) {
              setUnreadCount(prev => ({
                ...prev,
                [fromId]: (prev[fromId] || 0) + 1,
              }));
            }
          } else if (msg.type === 'dm_sent') {
            // Echo of our own sent message
            const dm = msg.message;
            const toId = dm.to;
            setMessages(prev => ({
              ...prev,
              [toId]: [...(prev[toId] || []), dm],
            }));
          }
        } catch (e) {
          console.error('Failed to parse presence message:', e);
        }
      };

      ws.onclose = () => {
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        reconnectRef.current = setTimeout(() => connect(), WS_RECONNECT_DELAY);
      };

      ws.onerror = () => {
        // onclose will fire after this
      };
    } catch (e) {
      console.error('Failed to create presence WebSocket:', e);
    }
  }, [getUserInfo]);

  useEffect(() => {
    if (!isLoggedIn) {
      setOnlineUsers([]);
      return;
    }

    connect();

    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [isLoggedIn, connect]);

  const totalUnread = Object.values(unreadCount).reduce((a, b) => a + b, 0);

  return {
    onlineUsers,
    count: onlineUsers.length,
    messages,
    sendMessage,
    activeChat,
    setActiveChat: openChat,
    closeChat: () => setActiveChat(null),
    unreadCount,
    totalUnread,
    // Min presence_key — sidebar måste använda samma värde som WS-identify
    // skickade, annars hamnar "filter ut mig själv" och "egna DMs"-check fel.
    myPresenceKey: getMyPresenceKey(),
  };
}

export default usePresence;
